'use client'

import { useCallback, useEffect, useState } from 'react'

import {
  getPortalOnboardingStatus,
  PORTAL_ONBOARDING_STATUS_EVENT,
  type PortalOnboardingStatus,
  setPortalOnboardingStatus,
} from '@common/utils/cookies'

export function usePortalOnboardingPersistence() {
  const [status, setStatus] = useState<PortalOnboardingStatus | null>(() =>
    typeof document === 'undefined' ? null : getPortalOnboardingStatus()
  )

  useEffect(() => {
    if (typeof window === 'undefined') return

    const syncFromCookie = () => {
      setStatus(getPortalOnboardingStatus())
    }

    const handleStatusChange = (event: Event) => {
      const customEvent = event as CustomEvent<PortalOnboardingStatus | null>
      setStatus(customEvent.detail ?? getPortalOnboardingStatus())
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncFromCookie()
      }
    }

    window.addEventListener(PORTAL_ONBOARDING_STATUS_EVENT, handleStatusChange)
    window.addEventListener('focus', syncFromCookie)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener(PORTAL_ONBOARDING_STATUS_EVENT, handleStatusChange)
      window.removeEventListener('focus', syncFromCookie)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  const markCompleted = useCallback(() => {
    setPortalOnboardingStatus('completed')
    setStatus('completed')
  }, [])

  const markDismissed = useCallback(() => {
    setPortalOnboardingStatus('dismissed')
    setStatus('dismissed')
  }, [])

  const shouldAutoStart = status === null

  return {
    status,
    shouldAutoStart,
    markCompleted,
    markDismissed,
  }
}
